import React, { useState } from 'react';
import { IoChevronDownOutline } from 'react-icons/io5';
import { canUse, canUseProject, catalogEntry } from '../../config/catalog';
import type { CatalogId } from '../../config/catalog';
import { areaCountry } from '../../config/countries';
import { geoTypeLabel } from '../../config/products';
import type { CountryCode } from '../../config/countries';
import { areaCount } from '../../utils/selectionSummary';
import { pluralize } from '../../utils/plural';
import { readToggles, writeToggles } from '../../utils/sessionToggles';
import type { SelectedArea } from '../../utils/odmSelection';
import AreaCard, { AreaIdBadges } from './AreaCard';
import AreaGroups from './AreaGroups';
import AreaThumbnail from './AreaThumbnail';

export { AreaIdBadges };

const ChevronIcon = IoChevronDownOutline as any;

/** Type groups start open while the compatible list is this short, closed beyond it. */
export const OPEN_GROUPS_MAX_CARDS = 12;

/** What the picker needs to know about a saved area. */
export interface PickerArea {
  id: string;
  name: string;
  description?: string;
  type: string;
  ids?: string[];
  geometry?: string;
  country_code?: string | null;
}

/** An ordered block of the list: projects first, then selected or monitored, then the rest. */
export interface AreaPickerGroup<T> {
  id: string;
  /** Without a title the areas are listed with no heading above them. */
  title?: string;
  areas: T[];
  /** A smaller, muted heading (used under a page that already has a big one). */
  quietTitle?: boolean;
}

export interface AreaPickerProject<T> {
  id: string;
  name: string;
  members: T[];
}

/** What a page may add to a row. */
export interface RowExtras {
  /** Next to the name (the draft-data badge). */
  badge?: React.ReactNode;
  /** Top right of the row (edit and delete). */
  actions?: React.ReactNode;
  /** Under the description and the count (id badges, a "live" badge). */
  body?: React.ReactNode;
  /** The bottom of the row (the monitoring toggle, "Analyze Activities"). */
  footer?: React.ReactNode;
}

export interface RowState {
  supported: boolean;
  ok: boolean;
  reason: string | null;
  picked: boolean;
}

export interface AreaPickerProps<T extends PickerArea> {
  /** The product whose rules say which areas can be used. */
  product: CatalogId;
  /** When set, only areas of this country are listed. */
  country?: CountryCode | null;
  /** 'multi' toggles areas in and out of the selection, 'single' replaces it. */
  mode: 'single' | 'multi';
  selection: readonly SelectedArea[];
  /** Given, rows get a checkbox (multi) or radio (single); absent, the list has no selection UI. */
  onChange?: (area: T, picked: boolean) => void;
  groups: AreaPickerGroup<T>[];
  projects?: AreaPickerProject<T>[];
  projectsTitle?: string;
  /** Multi mode: pick a project's usable members together. */
  onToggleProject?: (members: T[]) => void;
  /** Level of the group and project headings (the countries below them are level 3). */
  headingLevel?: 2 | 3;
  /** Muted line under the lists. */
  hiddenNote?: string | null;
  renderRowExtras?: (area: T, state: RowState) => RowExtras;
  /** Visible text beside a row's checkbox ("Add to project"); the checkbox is named "Select <name>" anyway. */
  selectLabel?: string;
  /** Also show a (disabled) checkbox on areas the product cannot use, so the reason sits beside it. */
  showUnsupportedSelect?: boolean;
  /** Show "3 municipalities selected" / "Custom polygon" under the description. Default true. */
  showCounts?: boolean;
  /**
   * Multi mode: 'card' (default) makes the whole card the checkbox; 'checkbox' puts a visible checkbox, labelled
   * `selectLabel`, on the card instead, for cards whose footer is full of controls of its own.
   */
  selectBy?: 'card' | 'checkbox';
}

const Heading: React.FC<{ level: 2 | 3; id: string; className: string; children: React.ReactNode }> = ({
  level,
  id,
  className,
  children,
}) => (level === 2 ? (
  <h2 id={id} className={className}>{children}</h2>
) : (
  <h3 id={id} className={className}>{children}</h3>
));

function AreaPicker<T extends PickerArea>({
  product,
  country,
  mode,
  selection,
  onChange,
  groups,
  projects = [],
  projectsTitle = 'Projects',
  onToggleProject,
  headingLevel = 3,
  hiddenNote,
  renderRowExtras,
  selectLabel,
  showCounts = true,
  showUnsupportedSelect = false,
  selectBy = 'card',
}: AreaPickerProps<T>) {
  const [notCompatOpen, setNotCompatOpen] = useState<boolean>(() => readToggles(`${product}:picker`).notCompatible ?? false);
  const pickedIds = new Set(selection.map((s) => s.id));
  const inScope = (a: T) => country == null || areaCountry(a) === country;

  const renderRow = (area: T, supported: boolean) => {
    const use = canUse(product, area, selection);
    const picked = pickedIds.has(area.id);
    // An unsupported area (the "Not Supported" block) is never selectable, whatever the selection.
    const reason = supported ? use.reason : canUse(product, area, []).reason;
    const ok = supported && use.ok;
    const extras = renderRowExtras?.(area, { supported, ok, reason, picked }) ?? {};
    const c = areaCountry(area);
    const selectable = onChange !== undefined && (supported || showUnsupportedSelect);
    // A picked card stays clickable so it can be unpicked; any other card the rules refuse is dimmed with its reason.
    const disabled = !ok && !picked ? { reason: reason ?? 'Not supported' } : undefined;
    const byCheckbox = selectable && mode === 'multi' && selectBy === 'checkbox';
    const footer = (
      <>
        {selectable && selectLabel && !byCheckbox ? <p className="text-xs font-medium text-ink-muted mb-2">{selectLabel}</p> : null}
        {showCounts && (
          <p className="text-xs text-ink-faint mb-3">
            {area.ids ? `${areaCount(area.ids.length, area.type, c)} selected` : 'Custom polygon'}
          </p>
        )}
        {extras.body}
        {picked && !ok && reason ? <p className="text-xs text-warn-ink">{reason}</p> : null}
        {ok && extras.footer ? extras.footer : null}
      </>
    );
    return (
      <AreaCard
        area={area}
        selected={picked}
        disabled={disabled}
        onClick={onChange && selectable && !byCheckbox ? () => onChange(area, !picked) : undefined}
        checkbox={onChange && byCheckbox ? { label: selectLabel ?? 'Select', onChange: () => onChange(area, !picked) } : undefined}
        role={mode === 'multi' ? 'checkbox' : 'radio'}
        headingLevel={5}
        badges={extras.badge}
        actions={extras.actions}
        footer={footer}
      />
    );
  };

  const supportedAlone = (a: T) => canUse(product, a, []).supported;
  const visibleProjects = projects
    .map((p) => ({ ...p, members: p.members.filter(inScope) }))
    .filter((p) => p.members.length > 0);
  // A project no member of which the product can ever use goes to Not compatible.
  const usableProjects = visibleProjects.filter((p) => p.members.some(supportedAlone));
  const unusableProjects = visibleProjects.filter((p) => !p.members.some(supportedAlone));

  const scopedGroups = groups
    .map((g) => ({ ...g, areas: g.areas.filter(inScope) }))
    .map((g) => ({ ...g, compatible: g.areas.filter(supportedAlone) }))
    .filter((g) => g.compatible.length > 0);
  const incompatibleAreas = groups.flatMap((g) => g.areas.filter(inScope).filter((a) => !supportedAlone(a)));
  const compatibleCount = scopedGroups.reduce((n, g) => n + g.compatible.length, 0);
  const collapseGroups = compatibleCount > OPEN_GROUPS_MAX_CARDS;

  const incompatibleCount = incompatibleAreas.length + unusableProjects.length;
  const summary = (() => {
    const labels = Array.from(new Set(incompatibleAreas.map((a) => geoTypeLabel(a.type, areaCountry(a)))));
    const title = catalogEntry(product).title;
    if (labels.length === 0) return `Projects with no area ${title} can use`;
    if (labels.length <= 2) return `${labels.join(' and ')} aren't supported for ${title}`;
    return `${pluralize(labels.length, 'area type')} aren't supported for ${title}`;
  })();

  const toggleNotCompat = () => {
    const next = !notCompatOpen;
    setNotCompatOpen(next);
    writeToggles(`${product}:picker`, { ...readToggles(`${product}:picker`), notCompatible: next });
  };

  const renderProjects = (list: typeof visibleProjects, usable: boolean) =>
    mode === 'multi' && onToggleProject ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pl-2">
        {list.map((p) => {
          const result = canUseProject(product, p.members, selection);
          const picked = result.ok && result.areas.every((a) => pickedIds.has(a.id));
          return (
            <article key={p.id} aria-label={p.name} className="card bg-base-100 shadow overflow-hidden">
              <AreaThumbnail areas={p.members} label={`Outline of ${p.name}`} />
              <div className="card-body p-4">
                <label className={`flex items-start gap-3 ${result.ok ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}>
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary mt-1"
                    aria-label={`Select project ${p.name}`}
                    checked={picked}
                    disabled={!result.ok}
                    onChange={() => onToggleProject(p.members)}
                  />
                  <span className="flex-1">
                    <span className="card-title text-base">{p.name}</span>
                    <span className="block text-xs text-base-content/60 mt-1">{pluralize(p.members.length, 'area')}</span>
                  </span>
                </label>
                {!result.ok ? <p className="text-xs text-warning">{result.reason}</p> : null}
              </div>
            </article>
          );
        })}
      </div>
    ) : (
      <div className="space-y-3 pl-2">
        {list.map((p) => (
          <div key={p.id} className="collapse collapse-arrow bg-base-100 shadow">
            <input type="checkbox" defaultChecked={usable} aria-label={`Show ${p.name}`} />
            <div className="collapse-title text-xl font-medium">
              {p.name}
              <span className="ml-2 badge badge-primary">{p.members.length}</span>
            </div>
            <div className="collapse-content">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
                {p.members.map((a) => (
                  <React.Fragment key={a.id}>{renderRow(a, supportedAlone(a))}</React.Fragment>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    );

  return (
    <div>
      {usableProjects.length > 0 && (
        <section className="mb-8" aria-labelledby="picker-projects">
          <Heading level={headingLevel} id="picker-projects" className="text-xl font-bold mb-3 flex items-center gap-2">
            <span>📁</span>
            <span>{projectsTitle}</span>
            <span className="badge badge-neutral badge-sm">{usableProjects.length}</span>
          </Heading>
          {renderProjects(usableProjects, true)}
        </section>
      )}

      <div className="space-y-10">
        {scopedGroups.map((g) => {
          const list = (
            <AreaGroups
              areas={g.compatible}
              headingLevel={3}
              renderCard={(a) => renderRow(a, true)}
              defaultCollapsed={collapseGroups}
              persistKey={`${product}:picker:${g.id}`}
            />
          );
          if (!g.title) return <React.Fragment key={g.id}>{list}</React.Fragment>;
          const headingId = `picker-${g.id}`;
          return (
            <section key={g.id} aria-labelledby={headingId}>
              <Heading
                level={headingLevel}
                id={headingId}
                className={g.quietTitle ? 'mb-2 text-sm font-semibold text-base-content/70' : 'text-xl font-bold mb-3'}
              >
                {g.title}
              </Heading>
              {list}
            </section>
          );
        })}
        {incompatibleCount > 0 && (
          <section data-testid="not-compatible" aria-labelledby="picker-not-compatible">
            <button
              type="button"
              aria-expanded={notCompatOpen}
              onClick={toggleNotCompat}
              className="w-full flex items-center justify-between gap-3 rounded-2xl border border-paper-300/70 bg-surface px-5 py-4 text-left hover:bg-paper-100 transition-colors"
            >
              <span>
                <Heading level={headingLevel} id="picker-not-compatible" className="text-xl font-bold flex items-center gap-2">
                  <span>Not compatible</span>
                  <span className="badge badge-neutral badge-sm">{incompatibleCount}</span>
                </Heading>
                <span className="block text-sm text-base-content/60 mt-1">{summary}</span>
              </span>
              <ChevronIcon className={`h-5 w-5 shrink-0 text-ink-faint transition-transform duration-300 ${notCompatOpen ? 'rotate-180' : ''}`} />
            </button>
            {notCompatOpen && (
              <div className="mt-6 space-y-8">
                {unusableProjects.length > 0 && renderProjects(unusableProjects, false)}
                {incompatibleAreas.length > 0 && (
                  <AreaGroups areas={incompatibleAreas} headingLevel={3} renderCard={(a) => renderRow(a, false)} />
                )}
              </div>
            )}
          </section>
        )}
        {hiddenNote && <p className="text-xs text-base-content/60">{hiddenNote}</p>}
      </div>
    </div>
  );
}

export default AreaPicker;
